// Export these for use in other components
import { useCallback, useState, useMemo, useEffect } from 'react';

import { useFormContext } from 'react-hook-form';

import { useStores } from 'mobx/hooks/useStores';

import { TwoWayMessagesFetcher } from 'fetchers/TwoWayMessagesFetcher';

import { formatDate } from 'utils/DateUtils';
import { formatInternationalNumber } from 'utils/FormatUtils';
import { isValidPhoneNumber } from 'utils/ValidationUtils';

import { FEATURES } from 'constants/features';

import { IPatientContact, RequestConsentMessageStatus } from 'models/Patient';
import { PhoneType } from 'models/PhoneNumberDetails';

import { usePatientModel } from 'components/Patient/usePatientModel';
import {
  NEW_CONTACT_VALUE,
  PATIENT_IS_CONTACT_VALUE
} from 'components/Ticket/TicketForms/ticket.shared';

const RESEND_COOLDOWN_PERIOD = 30000;

export const useConsentVerification = () => {
  const patient = usePatientModel();
  const { watch, setValue, getValues } = useFormContext();
  const { patientPageStore, settingsStore } = useStores();
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [lastSentTime, setLastSentTime] = useState<number | null>(null);
  const [verificationResponseReceived, setVerificationResponseReceived] = useState(false);

  const isConsentVerificationEnabled = settingsStore.hasFeature(
    FEATURES.MESSAGING_CONSENT_VERIFICATION
  );

  const { contactId, countryCode, phone, callbackName, callbackRelationship } = watch();

  // Track verification response from polling - show final state when we get any definitive response
  useEffect(
    function trackVerificationResponse() {
      if (patient?.isMessagingConsented === true || patient?.isMessagingConsented === false) {
        setVerificationResponseReceived(true);
      }
    },
    [patient?.isMessagingConsented]
  );

  const resetVerificationFlow = useCallback(() => {
    setVerificationResponseReceived(false);
  }, []);

  // Contact state
  const isPatientContact = !contactId || contactId === PATIENT_IS_CONTACT_VALUE;
  const recipientName = isPatientContact ? patient?.fullName : callbackName;

  const isCurrentPhoneNumberValid = isValidPhoneNumber(
    formatInternationalNumber(countryCode, phone)
  );

  const isNewContactInfoIncomplete =
    contactId === NEW_CONTACT_VALUE &&
    (!callbackName || !callbackRelationship || !isCurrentPhoneNumberValid);

  const isLandline = useMemo(() => {
    const patientIsContact = contactId === PATIENT_IS_CONTACT_VALUE;
    const contactPhoneType = patientIsContact
      ? (patient?.phoneType as PhoneType)
      : patient?.getContactById(Number(contactId))?.phoneType;
    return contactPhoneType === PhoneType.landline;
  }, [contactId, patient]);

  const isAlreadyConsented = patient?.isMessagingConsented === true;
  const isConsentDeclined = patient?.isMessagingConsented === false;

  const createContactIfNeeded = useCallback(async () => {
    if (contactId === NEW_CONTACT_VALUE && patient?.id) {
      const formValues = getValues();
      const contactInfo: IPatientContact = {
        name: formValues.callbackName,
        relationship: formValues.callbackRelationship?.value,
        phoneNumber: `${formValues.countryCode}${formValues.phone}`,
        phoneExtension: formValues.phoneExtension || null,
        language: formValues.language.value
      };

      const newContact = await patientPageStore.createContact(patient.id, contactInfo);

      if (newContact?.id) {
        setValue('contactId', String(newContact.id));
        return String(newContact.id);
      }

      return null;
    } else if (
      contactId !== PATIENT_IS_CONTACT_VALUE &&
      contactId !== NEW_CONTACT_VALUE &&
      patient?.id
    ) {
      // Handle existing contact updates
      const formValues = getValues();
      const existingContact = patient.contacts?.find((contact) => contact.id === Number(contactId));

      if (existingContact) {
        const hasChanges =
          existingContact.name !== formValues.callbackName ||
          existingContact.relationship !== formValues.callbackRelationship?.value ||
          existingContact.phoneNumber !== `${formValues.countryCode}${formValues.phone}` ||
          existingContact.phoneExtension !== (formValues.phoneExtension || null);

        if (hasChanges) {
          const updatedContactInfo: IPatientContact = {
            name: formValues.callbackName,
            relationship: formValues.callbackRelationship?.value,
            phoneNumber: `${formValues.countryCode}${formValues.phone}`,
            phoneExtension: formValues.phoneExtension || null
          };

          await patientPageStore.updateContact(patient.id, Number(contactId), updatedContactInfo);
        }
      }
    }

    return contactId;
  }, [contactId, getValues, patient?.id, patient?.contacts, patientPageStore, setValue]);

  const handleSendConsentRequest = useCallback(async () => {
    if (!patient || isMessageSending) {
      return;
    }

    // Check if a message was sent in the last 30 seconds
    const now = Date.now();
    if (lastSentTime && now - lastSentTime < RESEND_COOLDOWN_PERIOD) {
      return;
    }

    setIsMessageSending(true);

    // Reset verification state since server will reset fields on new request
    setVerificationResponseReceived(false);

    try {
      // Contact is guaranteed to be created/updated by parent component before reaching here
      const resolvedContactId =
        contactId === PATIENT_IS_CONTACT_VALUE ? PATIENT_IS_CONTACT_VALUE : contactId;

      await TwoWayMessagesFetcher.sendConsentRequest(patient.id, {
        ...(resolvedContactId !== PATIENT_IS_CONTACT_VALUE &&
          resolvedContactId && { contactId: Number(resolvedContactId) })
      });

      // Force UI update - set status to pending immediately for UI feedback
      if (patient) {
        // This is a temporary UI update to show the verification is pending
        // The actual state will be updated when patientPageStore.updateConsentStatusOnly completes
        patient.requestConsentMessageStatus = RequestConsentMessageStatus.Pending;
      }

      // Force refresh patient consent status to get updated status
      patientPageStore.updateConsentStatusOnly(patient.id);
    } finally {
      setResendDisabled(true);
      // After sending the message, reset resend timer
      setTimeout(() => {
        setResendDisabled(false);
      }, RESEND_COOLDOWN_PERIOD); // 30 seconds

      setIsMessageSending(false);
      setLastSentTime(Date.now());
    }
  }, [isMessageSending, lastSentTime, patient, patientPageStore, contactId]);

  const getVerificationDate = () => {
    if (patient?.messagingConsent) {
      return formatDate(patient.messagingConsent, 'MM/DD/YYYY');
    }
    return '';
  };

  return {
    isConsentVerificationEnabled,
    recipientName,
    isNewContactInfoIncomplete,
    isLandline,
    isMessageSending,
    resendDisabled,
    isAlreadyConsented,
    isConsentDeclined,
    verificationResponseReceived,
    handleSendConsentRequest,
    getVerificationDate,
    resetVerificationFlow,
    createContactIfNeeded
  };
};
